/**
 * Módulo UI: layout y helpers para la interfaz del juego.
 *
 * Todas las posiciones están en coords 360x800 (resolución interna del juego),
 * que es el BKG.png (900x2000) escalado por 2.5x. Vite sirve las imágenes
 * desde /public/ui.
 */
import Phaser from "phaser";
import { FONT } from "./types";

// ═══════════════════════════ LAYOUT (coords 360x800) ═══════════════════════
// El viewport es el "televisor" negro donde se renderiza el mundo/combate.
export const VIEWPORT = { x: 22, y: 100, w: 316, h: 240 } as const;
export const VIEWPORT_CENTER = { x: VIEWPORT.x + VIEWPORT.w / 2, y: VIEWPORT.y + VIEWPORT.h / 2 };

const LAYOUT = {
  MUSIC_BAR:   { x: 180, y: 24,  w: 315, h: 34 },
  TOP_MAP:     { x: 72,  y: 76,  w: 90,  h: 32 },
  TOP_INV:     { x: 180, y: 76,  w: 90,  h: 32 },
  TOP_OPT:     { x: 288, y: 76,  w: 90,  h: 32 },

  // Barras: alineadas a la izquierda desde x=24. Anchas pero no tanto que
  // pisen los botones ENTER/RETURN a la derecha.
  HP_BAR:      { x: 24, y: 358, w: 170, h: 18 },
  XP_BAR:      { x: 24, y: 382, w: 170, h: 18 },
  BTN_ENTER:   { x: 278, y: 358, w: 110, h: 28 },
  BTN_RETURN:  { x: 278, y: 388, w: 110, h: 28 },

  // D-pad: 4 botones en cruz. Cada botón 44x44.
  DPAD: { cx: 72, cy: 488 },
  DPAD_OFFSET: 42,
  DPAD_SIZE: 44,

  // 2x2 grid de botones de acción
  BTN_1: { x: 202, y: 468, w: 92, h: 32 },
  BTN_3: { x: 300, y: 468, w: 92, h: 32 },
  BTN_2: { x: 202, y: 508, w: 92, h: 32 },
  BTN_4: { x: 300, y: 508, w: 92, h: 32 },

  // 6 slots de pet (3x2)
  PET_SLOTS: [
    { x: 66,  y: 598 }, { x: 180, y: 598 }, { x: 294, y: 598 },
    { x: 66,  y: 698 }, { x: 180, y: 698 }, { x: 294, y: 698 },
  ],
  PET_SLOT_SIZE: 96,
} as const;

// ═══════════════════════════ PRELOAD ══════════════════════════════════════
export function preloadUI(scene: Phaser.Scene): void {
  const P = "/ui";
  // Background & bars
  scene.load.image("ui_bkg", `${P}/BKG.png`);
  scene.load.image("ui_bar_music", `${P}/BAR_MUSIC.png`);
  scene.load.image("ui_bar_music_full", `${P}/BAR_MUSIC_FULL.png`);
  scene.load.image("ui_bar_empty", `${P}/BAR_EMPTY.png`);
  scene.load.image("ui_bar_red", `${P}/BAR_RED_FULL.png`);
  scene.load.image("ui_bar_blue", `${P}/BAR_BLUE_FULL.png`);
  scene.load.image("ui_bar_yellow", `${P}/BAR_YELLOW_FULL.png`);

  // Top buttons
  for (const n of ["MAP", "INVENTORY", "OPTIONS"]) {
    scene.load.image(`ui_btn_${n.toLowerCase()}`, `${P}/BUTTON_${n}.png`);
    scene.load.image(`ui_btn_${n.toLowerCase()}_down`, `${P}/BUTTON_${n}_DOWN.png`);
  }

  // Action buttons (1-4, ENTER, RETURN)
  for (const n of ["1", "2", "3", "4", "ENTER", "RETURN"]) {
    scene.load.image(`ui_btn_${n.toLowerCase()}`, `${P}/BUTTON_${n}.png`);
    scene.load.image(`ui_btn_${n.toLowerCase()}_down`, `${P}/BUTTON_${n}_DOWN.png`);
  }

  // Joystick (d-pad)
  for (const n of ["UP", "DOWN", "LEFT", "RIGHT"]) {
    scene.load.image(`ui_dpad_${n.toLowerCase()}`, `${P}/JOYSTICK_${n}.png`);
    scene.load.image(`ui_dpad_${n.toLowerCase()}_down`, `${P}/JOYSTICK_${n}_DOWN.png`);
  }

  // Pet inventory slots
  scene.load.image("ui_pet_empty", `${P}/PET_INVENTORY_EMPTY.png`);
  scene.load.image("ui_pet_normal", `${P}/PET_INVENTORY_NORMAL.png`);
  scene.load.image("ui_pet_crystal", `${P}/PET_INVENTORY_CRYSTAL50PERCENT.png`);
}

// ═══════════════════════════ BACKGROUND ═══════════════════════════════════
export function addBackground(scene: Phaser.Scene): Phaser.GameObjects.Image {
  const img = scene.add.image(180, 400, "ui_bkg");
  img.setDisplaySize(360, 800);
  return img;
}

// ═══════════════════════════ VIEWPORT CLIP ════════════════════════════════
/** Crea un contenedor clipeado al área del viewport. Ideal para el mapa. */
export function createViewportMask(scene: Phaser.Scene): Phaser.Display.Masks.GeometryMask {
  const shape = scene.make.graphics();
  shape.fillStyle(0xffffff);
  shape.fillRect(VIEWPORT.x, VIEWPORT.y, VIEWPORT.w, VIEWPORT.h);
  return shape.createGeometryMask();
}

// ═══════════════════════════ BARRAS HP/XP ═════════════════════════════════
export class ProgressBar {
  private empty: Phaser.GameObjects.Image;
  private fill: Phaser.GameObjects.Image;
  private label: Phaser.GameObjects.Text;
  private w: number;

  constructor(
    scene: Phaser.Scene,
    pos: { x: number; y: number; w: number; h: number },
    fillKey: string,
    labelText = "",
  ) {
    this.w = pos.w;
    this.empty = scene.add.image(pos.x, pos.y, "ui_bar_empty").setOrigin(0, 0.5);
    this.empty.setDisplaySize(pos.w, pos.h);
    this.fill = scene.add.image(pos.x, pos.y, fillKey).setOrigin(0, 0.5);
    this.fill.setDisplaySize(pos.w, pos.h);
    this.label = scene.add.text(pos.x + pos.w / 2, pos.y, labelText, {
      fontFamily: FONT, fontSize: "10px", fontStyle: "bold", color: "#fff",
      stroke: "#000", strokeThickness: 2,
    }).setOrigin(0.5);
  }

  setProgress(current: number, max: number, labelOverride?: string): void {
    const ratio = Math.max(0, Math.min(1, current / max));
    this.fill.setDisplaySize(this.w * ratio, this.fill.displayHeight);
    this.label.setText(labelOverride ?? `${Math.max(0, Math.floor(current))}/${max}`);
  }

  destroy(): void {
    this.empty.destroy();
    this.fill.destroy();
    this.label.destroy();
  }
}

export function createHPBar(scene: Phaser.Scene): ProgressBar {
  return new ProgressBar(scene, LAYOUT.HP_BAR, "ui_bar_red");
}

export function createXPBar(scene: Phaser.Scene): ProgressBar {
  return new ProgressBar(scene, LAYOUT.XP_BAR, "ui_bar_blue");
}

export function createWildHPBar(scene: Phaser.Scene): ProgressBar {
  return new ProgressBar(scene, LAYOUT.XP_BAR, "ui_bar_yellow");
}

// ═══════════════════════════ BOTONES ══════════════════════════════════════
export interface ImageButton {
  image: Phaser.GameObjects.Image;
  setEnabled(enabled: boolean): void;
  setLabel(text: string): void;  // actualiza label de contexto (si existe)
  destroy(): void;
}

/**
 * Crea un botón a partir de un par de imágenes (normal / pressed).
 * Los botones con texto horneado (ENTER, RETURN, 1-4, MAP, etc.) NO reciben
 * un label encima: el texto ya está en el PNG.
 *
 * Si pasás `contextLabel`, se dibuja un texto pequeño arriba del botón
 * para indicar qué hace (ej: "HUIR" arriba del botón RETURN en combate).
 */
function makeImageButton(
  scene: Phaser.Scene,
  pos: { x: number; y: number; w: number; h: number },
  key: string,
  keyDown: string,
  onClick: () => void,
  contextLabel = "",
  labelPos: "above" | "below" = "above",
): ImageButton {
  const img = scene.add.image(pos.x, pos.y, key).setOrigin(0.5, 0.5);
  img.setDisplaySize(pos.w, pos.h);

  let ctx: Phaser.GameObjects.Text | null = null;
  if (contextLabel) {
    const yLabel = labelPos === "above"
      ? pos.y - pos.h / 2 - 5
      : pos.y + pos.h / 2 + 5;
    ctx = scene.add.text(pos.x, yLabel, contextLabel, {
      fontFamily: FONT, fontSize: "9px", fontStyle: "bold",
      color: "#f5c242", stroke: "#000", strokeThickness: 2,
    }).setOrigin(0.5, labelPos === "above" ? 1 : 0);
  }

  let enabled = true;
  img.setInteractive({ useHandCursor: true });
  img.on("pointerdown", () => {
    if (!enabled) return;
    img.setTexture(keyDown);
  });
  img.on("pointerup", () => {
    if (!enabled) return;
    img.setTexture(key);
    onClick();
  });
  img.on("pointerout", () => img.setTexture(key));

  return {
    image: img,
    setEnabled(v: boolean) {
      enabled = v;
      img.setAlpha(v ? 1 : 0.4);
      if (ctx) ctx.setAlpha(v ? 1 : 0.4);
    },
    setLabel(t: string) {
      if (ctx) ctx.setText(t);
    },
    destroy() { img.destroy(); if (ctx) ctx.destroy(); },
  };
}

/** Botones de arriba: MAP, INVENTORY, OPTIONS. Labels horneados en los assets. */
export function addTopButton(
  scene: Phaser.Scene,
  which: "map" | "inventory" | "options",
  onClick: () => void,
): ImageButton {
  const pos = which === "map" ? LAYOUT.TOP_MAP
            : which === "inventory" ? LAYOUT.TOP_INV
            : LAYOUT.TOP_OPT;
  return makeImageButton(scene, pos, `ui_btn_${which}`, `ui_btn_${which}_down`, onClick);
}

/**
 * Botones de acción 1-4. Slots 1/3 tienen label ARRIBA; 2/4 tienen label
 * ABAJO para que los dos labels de cada columna no se pisen.
 */
export function addActionButton(
  scene: Phaser.Scene,
  slot: 1 | 2 | 3 | 4,
  contextLabel: string,
  onClick: () => void,
): ImageButton {
  const pos = slot === 1 ? LAYOUT.BTN_1 : slot === 2 ? LAYOUT.BTN_2
            : slot === 3 ? LAYOUT.BTN_3 : LAYOUT.BTN_4;
  const labelPos = (slot === 1 || slot === 3) ? "above" : "below";
  return makeImageButton(scene, pos, `ui_btn_${slot}`, `ui_btn_${slot}_down`, onClick, contextLabel, labelPos);
}

/**
 * ENTER: acción primaria (confirmar/atacar/continuar).
 * No usamos context label arriba porque el espacio entre el viewport y el
 * botón es muy chico; el significado se entiende por el contexto de la escena.
 */
export function addEnterButton(scene: Phaser.Scene, _contextLabel: string, onClick: () => void): ImageButton {
  return makeImageButton(scene, LAYOUT.BTN_ENTER, "ui_btn_enter", "ui_btn_enter_down", onClick);
}

/** RETURN: acción secundaria (cancelar/huir/atrás). */
export function addReturnButton(scene: Phaser.Scene, _contextLabel: string, onClick: () => void): ImageButton {
  return makeImageButton(scene, LAYOUT.BTN_RETURN, "ui_btn_return", "ui_btn_return_down", onClick);
}

// ═══════════════════════════ D-PAD ════════════════════════════════════════
export type DpadDir = "up" | "down" | "left" | "right";

export function addDpad(scene: Phaser.Scene, onPress: (dir: DpadDir) => void): void {
  const { cx, cy } = LAYOUT.DPAD;
  const offset = LAYOUT.DPAD_OFFSET;

  const dirs: { dir: DpadDir; dx: number; dy: number }[] = [
    { dir: "up",    dx: 0, dy: -offset },
    { dir: "down",  dx: 0, dy: offset },
    { dir: "left",  dx: -offset, dy: 0 },
    { dir: "right", dx: offset, dy: 0 },
  ];

  for (const { dir, dx, dy } of dirs) {
    const img = scene.add.image(cx + dx, cy + dy, `ui_dpad_${dir}`).setOrigin(0.5, 0.5);
    img.setDisplaySize(LAYOUT.DPAD_SIZE, LAYOUT.DPAD_SIZE);
    img.setInteractive({ useHandCursor: true });
    img.on("pointerdown", () => {
      img.setTexture(`ui_dpad_${dir}_down`);
      onPress(dir);
    });
    img.on("pointerup", () => img.setTexture(`ui_dpad_${dir}`));
    img.on("pointerout", () => img.setTexture(`ui_dpad_${dir}`));
  }
}

// ═══════════════════════════ MUSIC BAR ════════════════════════════════════
export function addMusicBar(scene: Phaser.Scene): void {
  const p = LAYOUT.MUSIC_BAR;
  scene.add.image(p.x, p.y, "ui_bar_music").setOrigin(0.5, 0.5).setDisplaySize(p.w, p.h);
}

// ═══════════════════════════ PET SLOTS ════════════════════════════════════
export interface PetSlotView {
  update(creature: { sprite_key?: string; current_hp?: number; max_hp?: number } | null, active: boolean): void;
  destroy(): void;
}

export function addPetSlots(
  scene: Phaser.Scene,
  onSelect: (index: number) => void,
): PetSlotView[] {
  const slots: PetSlotView[] = [];
  for (let i = 0; i < 6; i++) {
    const pos = LAYOUT.PET_SLOTS[i];
    const size = LAYOUT.PET_SLOT_SIZE;
    const bg = scene.add.image(pos.x, pos.y, "ui_pet_empty").setDisplaySize(size, size);
    const sprite = scene.add.image(pos.x, pos.y - 6, "ui_pet_empty").setDisplaySize(50, 50).setVisible(false);
    const hpText = scene.add.text(pos.x, pos.y + 32, "", {
      fontFamily: FONT, fontSize: "9px", color: "#fff",
      stroke: "#000", strokeThickness: 2,
    }).setOrigin(0.5);
    const activeRing = scene.add.rectangle(pos.x, pos.y, size, size)
      .setStrokeStyle(2, 0xf5c242).setVisible(false);

    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerdown", () => onSelect(i));

    slots.push({
      update(creature, active) {
        if (creature && creature.sprite_key) {
          const tex = `creature_${creature.sprite_key}`;
          if (scene.textures.exists(tex)) {
            sprite.setTexture(tex).setVisible(true).setDisplaySize(60, 60);
          }
          if (creature.current_hp != null && creature.max_hp != null) {
            hpText.setText(`${creature.current_hp}/${creature.max_hp}`);
          }
          bg.setTexture(creature.current_hp === 0 ? "ui_pet_crystal" : "ui_pet_normal");
          activeRing.setVisible(active);
        } else {
          sprite.setVisible(false);
          hpText.setText("");
          bg.setTexture("ui_pet_empty");
          activeRing.setVisible(false);
        }
      },
      destroy() { bg.destroy(); sprite.destroy(); hpText.destroy(); activeRing.destroy(); },
    });
  }
  return slots;
}

// Re-export for scenes
export { LAYOUT };
